import { useState, useEffect } from "react";

// Detect system color scheme preference
const getSystemTheme = () => {
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
};

export const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    // If no saved theme, use system preference
    return savedTheme || getSystemTheme();
  });

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Colors matching your theme
    const darkBg = "#09090a";
    const lightBg = "#f6f4f0";

    // Add smooth transition
    root.style.transition = "background-color 0.3s ease, color 0.3s ease";
    body.style.transition = "background-color 0.3s ease";

    if (theme === "dark") {
      root.classList.add("dark");
      root.classList.remove("light");
      // Set background colors to prevent color mismatch
      // Background (ambient glow + --bg) comes from theme.css.
      root.style.backgroundColor = "";
      body.style.backgroundColor = "";
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
      // Set background colors to prevent color mismatch
      root.style.backgroundColor = "";
      body.style.backgroundColor = "";
    }
    localStorage.setItem("theme", theme);

    // Update meta theme color for mobile
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        theme === "dark" ? darkBg : lightBg,
      );
    }
  }, [theme]);

  const toggleTheme = async (clickEvent) => {
    const newTheme = theme === "dark" ? "light" : "dark";

    // Check if View Transitions API is supported
    if (!document.startViewTransition) {
      // Fallback: simple theme change
      setTheme(newTheme);
      return;
    }

    // Get click position for ripple effect
    const x = clickEvent?.clientX ?? window.innerWidth / 2;
    const y = clickEvent?.clientY ?? window.innerHeight / 2;

    // Calculate the radius for the circle (distance to farthest corner)
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    // Start view transition with custom animation
    const transition = document.startViewTransition(() => {
      setTheme(newTheme);
    });

    // Wait for transition to be ready, then animate
    await transition.ready;

    // Apply circular reveal animation
    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${endRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 600,
        easing: "ease-in-out",
        pseudoElement: "::view-transition-new(root)",
      },
    );
  };

  return { theme, toggleTheme };
};
